import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { RadioGroup, RadioGroupItem } from '@shared/components/ui/shadcn/radio-group';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import type { MigrationScope } from '../../adapters/MigrationApiAdapter';

interface StartMigrationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  childName: string;
  onStart: (params: { externalTeamId: string; stockFile?: File; scope: MigrationScope }) => Promise<void>;
  isPending?: boolean;
}

export const StartMigrationModal = ({
  open,
  onOpenChange,
  childName,
  onStart,
  isPending = false,
}: StartMigrationModalProps) => {
  const { t } = useTranslation('viewClientModule');
  const [externalTeamId, setExternalTeamId] = useState('');
  const [stockFile, setStockFile] = useState<File | null>(null);
  const [scope, setScope] = useState<MigrationScope>('total');
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setExternalTeamId('');
    setStockFile(null);
    setScope('total');
    setError(null);
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    const trimmed = externalTeamId.trim();
    if (!trimmed) {
      setError(t('tabs.migration.startModal.errors.externalTeamIdRequired'));
      return;
    }

    try {
      await onStart({
        externalTeamId: trimmed,
        stockFile: stockFile ?? undefined,
        scope,
      });
      handleClose();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      setError(message || t('tabs.migration.startModal.errorGeneric'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('tabs.migration.startModal.title')}</DialogTitle>
          <DialogDescription>{t('tabs.migration.startModal.description', { name: childName })}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="external-team-id">{t('tabs.migration.startModal.externalTeamId')}</Label>
            <Input
              id="external-team-id"
              value={externalTeamId}
              onChange={(e) => {
                setExternalTeamId(e.target.value);
                setError(null);
              }}
              placeholder={t('tabs.migration.startModal.externalTeamIdPlaceholder')}
              disabled={isPending}
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <div className="space-y-3">
            <Label>{t('tabs.migration.startModal.scopeLabel')}</Label>
            <RadioGroup
              value={scope}
              onValueChange={(v) => setScope(v as MigrationScope)}
              disabled={isPending}
              className="gap-3"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="total" id="migration-scope-total" />
                <Label htmlFor="migration-scope-total" className="font-normal cursor-pointer">
                  {t('tabs.migration.startModal.scopeTotal')}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="partial" id="migration-scope-partial" />
                <Label htmlFor="migration-scope-partial" className="font-normal cursor-pointer">
                  {t('tabs.migration.startModal.scopePartial')}
                </Label>
              </div>
            </RadioGroup>
          </div>
          <div className="space-y-2">
            <Label htmlFor="stock-file">{t('tabs.migration.startModal.stockFile')}</Label>
            <Input
              id="stock-file"
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              onChange={(e) => {
                const file = e.target.files?.[0];
                setStockFile(file ?? null);
              }}
              disabled={isPending}
            />
            <p className="text-xs text-muted-foreground">{t('tabs.migration.startModal.stockFileHelp')}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            {t('tabs.migration.startModal.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? t('tabs.migration.startModal.starting') : t('tabs.migration.startModal.start')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
